/**
 * The topology DOCUMENT: what an engineer drew, and nothing about what it is doing.
 *
 * The split from `derive.ts` is load-bearing. The prototype's solver wrote its scratch
 * (`_served`, `_lost`, `_soc`, `discharging`) back onto the config nodes and then
 * serialised the config wholesale, so live telemetry leaked into the saved "topology
 * document" — two exports of the same plant taken a second apart differed, which makes
 * the document useless as evidence. Nothing in this file may hold a solver result, and
 * every edit here returns a new document rather than mutating one.
 */

import type { SeriesBinding } from './binding';

export type { SeriesBinding };

/* ------------------------------------------------------------------- kinds */

export type Bus = 'ac' | 'dc';

/**
 * DECLARED path membership, and only for sources is it a fact rather than a claim.
 *
 * Naming which utility feed is "the A feed" is a human decision — the graph cannot know
 * it. Everything downstream of a source is a consequence, so `derive.ts` computes band
 * membership by reachability and treats this field on a carrier as an assertion to check,
 * never as an input to the redundancy verdict. That is the whole difference between this
 * module and the prototype it replaces.
 */
export type PathLabel = 'A' | 'B' | 'shared';

export type NodeKind =
  | 'utility' | 'genset' | 'solar'
  | 'ats' | 'switchgear' | 'sts'
  | 'transformer' | 'rectifier' | 'inverter' | 'ups'
  | 'dcbus' | 'pdu' | 'busway' | 'rpp'
  | 'battery'
  | 'rack' | 'cooling' | 'tenant' | 'load'
  | 'evac' | 'evdc';

export type KindCategory =
  | 'Sources' | 'Switching' | 'Conversion' | 'Distribution' | 'Storage' | 'Loads' | 'EV';

/**
 * How the derivation treats a kind. Behaviour is a property of the kind, not a flag on
 * the node, so a topology cannot contain a "load that is also a source" — which the
 * prototype allowed and which silently produces a graph with no meaningful direction.
 */
export type Behaviour = 'source' | 'storage' | 'carrier' | 'load';

/**
 * Glyphs are structured rather than raw markup so the catalogue stays data — an inline
 * `innerHTML` string here would put an XSS surface in a file that a future importer
 * writes into.
 */
export type GlyphShape =
  | { readonly s: 'path'; readonly d: string }
  | { readonly s: 'rect'; readonly x: number; readonly y: number; readonly w: number; readonly h: number; readonly rx: number }
  | { readonly s: 'circle'; readonly cx: number; readonly cy: number; readonly r: number };

export interface KindSpec {
  readonly label: string;
  readonly category: KindCategory;
  readonly bus: Bus;
  readonly behaviour: Behaviour;
  /** Nameplate default in kW. Batteries are 0 — their contribution is energy, not power. */
  readonly defaultKw: number;
  /** Three-letter id prefix. Short enough to read on a node card at 8px. */
  readonly code: string;
  readonly glyph: readonly GlyphShape[];
}

/*
 * Ported from the prototype's KINDS with its icon set intact. These are equipment
 * pictograms, NOT IEC 60617 single-line symbols: correct for an operator console,
 * and a deliberate gap to close before a diagram leaves the product as audit evidence.
 */
export const NODE_KINDS: Record<NodeKind, KindSpec> = {
  utility: {
    label: 'Utility Feed', category: 'Sources', bus: 'ac', behaviour: 'source', defaultKw: 2000, code: 'UTL',
    glyph: [{ s: 'path', d: 'M12 2v20M2 5h20m-9 0 7 7M12 5 5 12' }],
  },
  genset: {
    label: 'Generator', category: 'Sources', bus: 'ac', behaviour: 'source', defaultKw: 1000, code: 'GEN',
    glyph: [
      { s: 'rect', x: 3, y: 8, w: 18, h: 10, rx: 2 },
      { s: 'path', d: 'M7 8V6h10v2M11 11l-1.5 2.5h3L11 16M3 21h18' },
    ],
  },
  solar: {
    label: 'Solar / PV', category: 'Sources', bus: 'dc', behaviour: 'source', defaultKw: 250, code: 'PV',
    glyph: [
      { s: 'circle', cx: 12, cy: 12, r: 4 },
      { s: 'path', d: 'M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4' },
    ],
  },
  ats: {
    label: 'ATS', category: 'Switching', bus: 'ac', behaviour: 'carrier', defaultKw: 2000, code: 'ATS',
    glyph: [{ s: 'path', d: 'M3 7h6l3-3M3 7l3 3M21 17h-6l-3 3M21 17l-3-3M14 7h7M3 17h7' }],
  },
  switchgear: {
    label: 'Switchgear', category: 'Switching', bus: 'ac', behaviour: 'carrier', defaultKw: 2000, code: 'SWG',
    glyph: [
      { s: 'rect', x: 3, y: 3, w: 18, h: 18, rx: 2 },
      { s: 'path', d: 'M3 9h18M3 15h18M9 3v18M15 3v18' },
    ],
  },
  sts: {
    label: 'STS', category: 'Switching', bus: 'ac', behaviour: 'carrier', defaultKw: 400, code: 'STS',
    glyph: [{ s: 'path', d: 'M8 6 4 10l4 4M4 10h7a5 5 0 0 1 5 5v5M20 10l-4-4-4 4' }],
  },
  transformer: {
    label: 'Transformer', category: 'Conversion', bus: 'ac', behaviour: 'carrier', defaultKw: 1600, code: 'TFM',
    glyph: [{ s: 'path', d: 'M4 12a4 4 0 0 1 8 0 4 4 0 0 0 8 0M4 12v5M20 12v5M4 7V5M20 7V5' }],
  },
  rectifier: {
    label: 'Rectifier', category: 'Conversion', bus: 'dc', behaviour: 'carrier', defaultKw: 600, code: 'RCT',
    glyph: [
      { s: 'rect', x: 3, y: 4, w: 18, h: 16, rx: 2 },
      { s: 'path', d: 'M7 9l4 6M7 15l4-6M14 9v6M17 9v6' },
    ],
  },
  inverter: {
    label: 'Inverter', category: 'Conversion', bus: 'ac', behaviour: 'carrier', defaultKw: 250, code: 'INV',
    glyph: [
      { s: 'rect', x: 3, y: 4, w: 18, h: 16, rx: 2 },
      { s: 'path', d: 'M6 14h4l1-4 2 8 1-4h4' },
    ],
  },
  ups: {
    label: 'UPS', category: 'Conversion', bus: 'ac', behaviour: 'carrier', defaultKw: 800, code: 'UPS',
    glyph: [{ s: 'path', d: 'M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1 1 0 0 1 1.52 0C14.5 3.8 17 5 19 5a1 1 0 0 1 1 1z' }],
  },
  dcbus: {
    label: 'DC Bus', category: 'Distribution', bus: 'dc', behaviour: 'carrier', defaultKw: 1000, code: 'DCB',
    glyph: [{ s: 'path', d: 'M3 12h18M6 8v8M18 8v8M10 6v12M14 6v12' }],
  },
  pdu: {
    label: 'PDU', category: 'Distribution', bus: 'ac', behaviour: 'carrier', defaultKw: 400, code: 'PDU',
    glyph: [
      { s: 'rect', x: 2, y: 2, w: 20, h: 8, rx: 2 },
      { s: 'rect', x: 2, y: 14, w: 20, h: 8, rx: 2 },
      { s: 'path', d: 'M6 6h.01M6 18h.01' },
    ],
  },
  busway: {
    label: 'Busway', category: 'Distribution', bus: 'ac', behaviour: 'carrier', defaultKw: 400, code: 'BUS',
    glyph: [
      { s: 'rect', x: 3, y: 5, w: 18, h: 4, rx: 1 },
      { s: 'rect', x: 3, y: 11, w: 18, h: 4, rx: 1 },
      { s: 'rect', x: 3, y: 17, w: 18, h: 2, rx: 1 },
    ],
  },
  rpp: {
    label: 'RPP', category: 'Distribution', bus: 'ac', behaviour: 'carrier', defaultKw: 200, code: 'RPP',
    glyph: [
      { s: 'rect', x: 4, y: 3, w: 16, h: 18, rx: 2 },
      { s: 'path', d: 'M8 7h8M8 11h8M8 15h5' },
    ],
  },
  battery: {
    label: 'Battery', category: 'Storage', bus: 'dc', behaviour: 'storage', defaultKw: 0, code: 'BAT',
    glyph: [
      { s: 'rect', x: 2, y: 7, w: 16, h: 10, rx: 2 },
      { s: 'path', d: 'M22 11v2m-11-4-2.5 3.5h3L9 15' },
    ],
  },
  rack: {
    label: 'Rack Row', category: 'Loads', bus: 'ac', behaviour: 'load', defaultKw: 160, code: 'RCK',
    glyph: [
      { s: 'rect', x: 4, y: 4, w: 16, h: 16, rx: 2 },
      { s: 'rect', x: 9, y: 9, w: 6, h: 6, rx: 0 },
      { s: 'path', d: 'M15 2v2M15 20v2M9 2v2M9 20v2M2 15h2M2 9h2M20 15h2M20 9h2' },
    ],
  },
  /*
   * The load kind for every plant that is NOT a data hall or a telecom site.
   *
   * `rack` is "Rack Row" and its role renders as IT; `tenant` is "Telecom Load" and sits on
   * the DC bus; `cooling` is HVAC. Those are the only three loads this catalogue had, so a
   * chemicals works, a water plant, a rail depot and a hospital all had to borrow one — and
   * Jamshoro, a solar plant feeding a dye house, drew its inferred site load as a rack and
   * labelled it IT. Root `CLAUDE.md` §12 names views as the weak seam and says to extend by
   * ROWS, not by branches: this is the row.
   *
   * Deliberately generic. "Facility" is what the load is from the generating plant's side of
   * the hookup, whatever the facility does, and a name that guesses the process would be the
   * same mistake as IT in the other direction.
   */
  load: {
    label: 'Facility Load', category: 'Loads', bus: 'ac', behaviour: 'load', defaultKw: 400, code: 'LOAD',
    glyph: [
      { s: 'rect', x: 3, y: 6, w: 18, h: 13, rx: 2 },
      { s: 'path', d: 'M7 6 L12 2 L17 6' },
      { s: 'path', d: 'M9 12 H15' },
      { s: 'path', d: 'M9 15.5 H15' },
    ],
  },
  cooling: {
    label: 'Cooling', category: 'Loads', bus: 'ac', behaviour: 'load', defaultKw: 300, code: 'CLG',
    glyph: [{ s: 'path', d: 'M17.7 7.7a2.5 2.5 0 1 1 1.8 4.3H2M9.6 4.6A2 2 0 1 1 11 8H2m10.6 11.4A2 2 0 1 0 14 16H2' }],
  },
  tenant: {
    label: 'Telecom Load', category: 'Loads', bus: 'dc', behaviour: 'load', defaultKw: 3, code: 'TEN',
    glyph: [{ s: 'path', d: 'M5 16C2 13 2 7 5 4M8 13a4 4 0 0 1 0-6M16 7a4 4 0 0 1 0 6M19 4c3 3 3 9 0 12M11 19h2l1 3h-4z' }],
  },
  evac: {
    label: 'AC Charger', category: 'EV', bus: 'ac', behaviour: 'load', defaultKw: 22, code: 'EVA',
    glyph: [
      { s: 'rect', x: 5, y: 2, w: 11, h: 20, rx: 2 },
      { s: 'path', d: 'M8 6h5M8 9h5M10 13l-1.5 3h3L10 19M19 10v5' },
    ],
  },
  evdc: {
    label: 'DC Fast Charger', category: 'EV', bus: 'dc', behaviour: 'load', defaultKw: 150, code: 'EVD',
    glyph: [
      { s: 'rect', x: 5, y: 2, w: 11, h: 20, rx: 2 },
      { s: 'path', d: 'M9 6h3M9 9h3M19 9v6a2 2 0 0 1-3 0M16 13l3-3M9 14l-1 4h3l-1 4' },
    ],
  },
};

/** Palette order. Sources first because a diagram is read from its sources down. */
export const CATEGORY_ORDER: readonly KindCategory[] = [
  'Sources', 'Switching', 'Conversion', 'Distribution', 'Storage', 'Loads', 'EV',
];

export const ALL_KINDS = Object.keys(NODE_KINDS) as readonly NodeKind[];

export function kindsInCategory(cat: KindCategory): readonly NodeKind[] {
  return ALL_KINDS.filter((k) => NODE_KINDS[k].category === cat);
}

export function isSourceKind(k: NodeKind): boolean {
  return NODE_KINDS[k].behaviour === 'source';
}
export function isLoadKind(k: NodeKind): boolean {
  return NODE_KINDS[k].behaviour === 'load';
}
export function isStorageKind(k: NodeKind): boolean {
  return NODE_KINDS[k].behaviour === 'storage';
}

/**
 * A source that can be RELIED ON to serve the load at an arbitrary moment.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THE REDUNDANCY VERDICT CANNOT COUNT A PV ARRAY AS A SECOND FEED
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `isSourceKind` answers "does power originate here". This answers a different question —
 * "can I lose the other one and still be served" — and a solar array answers no, every
 * night, whatever its breaker says.
 *
 * Measured on Jamshoro, 2026-09-14. Wiring its five plant buses to the site load without
 * this distinction moved the site from `N / Single path / warn` to
 * `2N / Fault tolerant / pass` with ZERO single points of failure, on the strength of ten
 * PV arrays reaching the load. That site has one HESCO feed, no generator and no battery:
 * losing the feeder after sunset takes it dark. Routing everything through a main LV board
 * instead gave `N+1 / Redundant sources`, which is the same false claim one notch quieter.
 *
 * It is the same trap `deriveRedundancy` already records for orphan sources — "the
 * prototype counted any energised source, so an orphan live utility labelled 'B' with no
 * feeds at all pushed the whole site to 2N". Reaching a load is necessary and not
 * sufficient; so is being able to serve it when asked.
 *
 * A GENSET IS FIRM AND A PV ARRAY IS NOT, and the difference is dispatchability, not fuel:
 * a standby set starts when the plant needs it, which is the entire reason it is installed.
 * `sourceIsLive` in `derive.ts` already reads `n.running` for a genset and returns a flat
 * `true` for solar, with the comment "modelled as available whenever its breaker is closed"
 * — that assumption is fine for drawing the diagram and wrong for grading it.
 *
 * Storage is not here because it is not a source kind: a discharging battery is a countdown
 * and the verdict says so with its own `BATT` level, which outranks 2N.
 *
 * `source-firmness.test.ts` fails if a new source kind is added without a decision, so this
 * cannot be half-extended.
 */
const FIRM_SOURCE_KINDS: ReadonlySet<NodeKind> = new Set<NodeKind>(['utility', 'genset']);

export function isFirmSource(k: NodeKind): boolean {
  return isSourceKind(k) && FIRM_SOURCE_KINDS.has(k);
}

/**
 * A transfer switch connects exactly ONE of its inputs at a time.
 *
 * That is what separates an ATS or STS from a parallel bus, and it is what the flow
 * derivation needs to know: a busbar divides its demand across its live feeds, a transfer
 * switch does not. Without the distinction a utility and a standby generator on one ATS
 * each read half the site load.
 */
export function isTransferKind(k: NodeKind): boolean {
  return k === 'ats' || k === 'sts';
}

/* ------------------------------------------------------------------ document */

export interface Breaker {
  readonly state: 'closed' | 'open';
  /**
   * A protective trip, distinct from a deliberate open. Both de-energise, but only one
   * of them is someone's decision — an incident review needs to tell them apart.
   */
  readonly tripped: boolean;
}

export interface TopologyNode {
  readonly id: string;
  readonly kind: NodeKind;
  readonly name: string;
  readonly bus: Bus;
  readonly path: PathLabel;
  readonly x: number;
  readonly y: number;
  /** Edge list held on the SOURCE. There are no edge records — an edge IS this entry. */
  readonly feeds: readonly string[];
  readonly ratingKw: number;
  readonly breaker: Breaker;
  /** Utility only: the feed is present but dead. Meaningless on other kinds. */
  readonly failed: boolean;
  /** Genset only: a stopped genset with a closed breaker is still not a source. */
  readonly running: boolean;
  /** Provenance of a seeded node, so a re-seed can recognise what it already created. */
  readonly deviceId: number | null;
  /**
   * Where this node's READINGS come from, when that is not simply "all of `deviceId`".
   *
   * `deviceId` is identity; this is measurement. One controller can measure the mains,
   * the generator and the transfer at once, and a node bound to the whole device would
   * take all three families and leave its siblings blank. See `binding.ts` for the cases
   * that forced the split.
   *
   * Absent means the previous behaviour exactly: the whole of `deviceId`, or nothing.
   */
  readonly measuredBy?: readonly SeriesBinding[] | undefined;
  /**
   * Set when the node is not a device but something the TELEMETRY implies must exist.
   *
   * A real site's device inventory is rarely a whole power chain. JAZZMSC5 is the case that
   * forced this: it has rectifiers, a PDU, two UPSs and battery cabinets, and no device row
   * for the incoming mains or for anything the plant actually serves. Seeded literally, that
   * graph has no source and no load, so nothing energises, every card reads NO FEED, and the
   * verdict is "no load modelled" — a diagram that looks broken while the plant is fine.
   *
   * The endpoints are nonetheless EVIDENCED: the rectifiers report 411 V of AC input, which
   * only exists if a mains feed does; and they report 111.4 kW of DC load power, which only
   * exists if something is drawing it. So they are drawn, and marked, and `inferredWhy`
   * carries the measurement that justifies each one. A diagram must not silently invent
   * equipment, and it must not pretend a measured load is absent either.
   */
  readonly inferred?: boolean;
  /** The measurement that justifies an inferred node. Shown on the card and in the inspector. */
  readonly inferredWhy?: string;
}

export interface Nominal {
  readonly acVoltage: number;
  /** Signed, telecom convention: -48 means a -48 V DC plant. Use Math.abs to compute. */
  readonly dcVoltage: number;
  readonly frequency: number;
  readonly pf: number;
}

/**
 * How the document came to exist — NOT how any one edge was decided.
 *
 * `drawn` means a person authored it and is answerable for the wiring. It was added
 * 2026-09-14 because nothing could say that, and `iso.ts` consequently treats
 * `origin === 'devices'` as grounds to call every edge inferred, no matter what the nodes
 * themselves record. A document seeded from devices and then corrected by an engineer had
 * no way to stop claiming its corrections were guesses — and the SLD, reading provenance per
 * node, disagreed with the twin about the same document.
 *
 * Independent of the per-node `inferred` flag, and deliberately so: a DRAWN document may
 * still contain endpoints the platform proposed. Jamshoro is exactly that — an engineer's
 * as-built for the generation side, with the utility feed, the transformer, the LV board and
 * the site load still carrying `inferred` and an `inferredWhy` apiece.
 */
export type TopologyOrigin = 'blank' | 'devices' | 'drawn' | 'example' | 'preset';

export interface SiteTopology {
  readonly siteId: string | null;
  readonly siteName: string;
  readonly nominal: Nominal;
  readonly nodes: readonly TopologyNode[];
  readonly origin: TopologyOrigin;
}

/* ---------------------------------------------------------------- geometry */

export const NODE_W = 176;
export const NODE_H = 84;
/** The visible dot grid. */
export const GRID = 26;
/**
 * Half a grid cell. The prototype snapped to 10px against a 26px grid, so a "snapped"
 * node never aligned with anything the operator could see; at 13 every second step lands
 * on a dot.
 */
export const SNAP = GRID / 2;
export const COL_W = NODE_W + 64;
export const ROW_H = NODE_H + 86;

export function snap(v: number): number {
  return Math.round(v / SNAP) * SNAP;
}

/** Power leaves the bottom of a node and enters the top of the next. One flow axis. */
export function outAnchor(n: TopologyNode): { x: number; y: number } {
  return { x: n.x + NODE_W / 2, y: n.y + NODE_H };
}
export function inAnchor(n: TopologyNode): { x: number; y: number } {
  return { x: n.x + NODE_W / 2, y: n.y };
}

/* ----------------------------------------------------------------- lookups */

export function nodeById(t: SiteTopology, id: string): TopologyNode | undefined {
  return t.nodes.find((n) => n.id === id);
}

/** O(V) per call, like the prototype: there is no edge index to keep consistent. */
export function childrenOf(t: SiteTopology, id: string): readonly TopologyNode[] {
  const n = nodeById(t, id);
  if (!n) return [];
  return n.feeds.flatMap((f) => {
    const c = nodeById(t, f);
    return c ? [c] : [];
  });
}

export function parentsOf(t: SiteTopology, id: string): readonly TopologyNode[] {
  return t.nodes.filter((n) => n.feeds.includes(id));
}

export function loadsOf(t: SiteTopology): readonly TopologyNode[] {
  return t.nodes.filter((n) => isLoadKind(n.kind));
}

export function sourcesOf(t: SiteTopology): readonly TopologyNode[] {
  return t.nodes.filter((n) => isSourceKind(n.kind));
}

/** kW as it is written everywhere in the product: no false precision above 100. */
export function fmtKw(kw: number): string {
  return kw >= 100 ? String(Math.round(kw)) : kw.toFixed(1);
}

/* ------------------------------------------------------------------- edits */

/**
 * Monotonic per-kind id, not the prototype's `kind + Date.now()%10000` — that collided
 * on same-millisecond adds and wrapped every ten seconds, and a collision here silently
 * merges two pieces of equipment into one node.
 */
export function nextNodeId(t: SiteTopology, kind: NodeKind): string {
  const code = NODE_KINDS[kind].code;
  const used = new Set(t.nodes.map((n) => n.id));
  for (let i = 1; i < 10_000; i++) {
    const id = `${code}-${i}`;
    if (!used.has(id)) return id;
  }
  // Unreachable in practice; a diagram with 10k nodes of one kind is not a diagram.
  return `${code}-${used.size + 1}`;
}

export function makeNode(
  t: SiteTopology,
  kind: NodeKind,
  x: number,
  y: number,
): TopologyNode {
  const spec = NODE_KINDS[kind];
  return {
    id: nextNodeId(t, kind),
    kind,
    name: spec.label,
    bus: spec.bus,
    /*
     * 'shared' is the honest default: a node just dropped on the canvas is not part of
     * any claimed redundant path yet. In the prototype this was the wrong-by-default
     * case because the verdict READ this field; here the verdict reads the graph, so a
     * left-alone label costs nothing but an advisory.
     */
    path: 'shared',
    x: snap(x),
    y: snap(y),
    feeds: [],
    ratingKw: spec.defaultKw,
    breaker: { state: 'closed', tripped: false },
    failed: false,
    /*
     * Standby, not running. A generator that has not been started carries nothing — it is
     * AVAILABLE, which is a different claim. Defaulting it to running made every genset a
     * live source: the flow derivation gave it a share of the load (an idle machine shown
     * carrying 115 kW) and the verdict counted it as an independent source, so a single
     * utility with one generator headlined "N+1 · Redundant sources". The seed note below
     * already states the intended semantics — a generator backs up the path it sits on
     * rather than forming a second one.
     */
    running: false,
    deviceId: null,
  };
}

export function addNode(t: SiteTopology, node: TopologyNode): SiteTopology {
  return { ...t, nodes: [...t.nodes, node] };
}

/** Removes the node AND every reference to it. A dangling feed is an invisible edge. */
export function removeNode(t: SiteTopology, id: string): SiteTopology {
  return {
    ...t,
    nodes: t.nodes
      .filter((n) => n.id !== id)
      .map((n) => (n.feeds.includes(id) ? { ...n, feeds: n.feeds.filter((f) => f !== id) } : n)),
  };
}

export type NodePatch = Partial<Omit<TopologyNode, 'id'>>;

export function patchNode(t: SiteTopology, id: string, patch: NodePatch): SiteTopology {
  return { ...t, nodes: t.nodes.map((n) => (n.id === id ? { ...n, ...patch } : n)) };
}

export function moveNode(t: SiteTopology, id: string, x: number, y: number): SiteTopology {
  return patchNode(t, id, { x: snap(x), y: snap(y) });
}

/**
 * Retypes a node in place, keeping the operator's overrides.
 *
 * Needed because the device role catalogue cannot express everything a kind can — an
 * "EV charger" role says nothing about AC versus DC — so a seeded node has to be
 * correctable without deleting it and losing every edge attached to it.
 */
export function retypeNode(t: SiteTopology, id: string, kind: NodeKind): SiteTopology {
  const n = nodeById(t, id);
  if (!n || n.kind === kind) return t;
  const from = NODE_KINDS[n.kind];
  const to = NODE_KINDS[kind];
  return patchNode(t, id, {
    kind,
    // Only defaults are carried over; a hand-entered rating or bus is the operator's.
    ...(n.name === from.label ? { name: to.label } : {}),
    ...(n.ratingKw === from.defaultKw ? { ratingKw: to.defaultKw } : {}),
    ...(n.bus === from.bus ? { bus: to.bus } : {}),
    running: kind === 'genset' ? n.running : false,
  });
}

/** Toggle, so the same gesture that draws a feed detaches it (the prototype's model). */
export function toggleFeed(t: SiteTopology, from: string, to: string): SiteTopology {
  const src = nodeById(t, from);
  if (!src || from === to) return t;
  const has = src.feeds.includes(to);
  return patchNode(t, from, {
    feeds: has ? src.feeds.filter((f) => f !== to) : [...src.feeds, to],
  });
}

/* ------------------------------------------------------------------ layout */

/**
 * Longest-path layering: y is electrical depth, x is path identity.
 *
 * Within a layer nodes are ordered by a SIGNED path key (A −1, shared 0, B +1) and then
 * by the mean x of their already-placed parents. The prototype sorted by
 * `path.localeCompare`, which orders A < B < shared and therefore parked the shared tail
 * on the right-hand edge of its layer — one press of Reformat silently destroyed the
 * mirrored A-left / shared-centre / B-right grammar that makes a 2N diagram readable.
 */
export function layoutTopology(t: SiteTopology): SiteTopology {
  const nodes = t.nodes;
  if (nodes.length === 0) return t;

  const parents = new Map<string, string[]>();
  for (const n of nodes) parents.set(n.id, []);
  for (const n of nodes) {
    for (const f of n.feeds) {
      const list = parents.get(f);
      if (list) list.push(n.id);
    }
  }

  const layer = new Map<string, number>();
  const visiting = new Set<string>();
  const depth = (id: string): number => {
    const memo = layer.get(id);
    if (memo !== undefined) return memo;
    // A cycle is a modelling error, not a layout problem; treat the re-entry as a root
    // so layout still terminates and let `derive.canConnect` be the thing that refuses.
    if (visiting.has(id)) return 0;
    visiting.add(id);
    let best = 0;
    for (const p of parents.get(id) ?? []) best = Math.max(best, depth(p) + 1);
    visiting.delete(id);
    layer.set(id, best);
    return best;
  };
  for (const n of nodes) depth(n.id);

  const byLayer = new Map<number, TopologyNode[]>();
  for (const n of nodes) {
    const L = layer.get(n.id) ?? 0;
    const bucket = byLayer.get(L);
    if (bucket) bucket.push(n);
    else byLayer.set(L, [n]);
  }

  const pathKey = (p: PathLabel): number => (p === 'A' ? -1 : p === 'B' ? 1 : 0);
  const widest = Math.max(...[...byLayer.values()].map((a) => a.length), 1);
  const placed = new Map<string, number>();
  const out = new Map<string, { x: number; y: number }>();

  for (const L of [...byLayer.keys()].sort((a, b) => a - b)) {
    const arr = [...(byLayer.get(L) ?? [])];
    const bary = (n: TopologyNode): number => {
      const xs = (parents.get(n.id) ?? []).flatMap((p) => {
        const x = placed.get(p);
        return x === undefined ? [] : [x];
      });
      return xs.length === 0 ? 0 : xs.reduce((s, x) => s + x, 0) / xs.length;
    };
    arr.sort(
      (a, b) =>
        pathKey(a.path) - pathKey(b.path) || bary(a) - bary(b) || a.id.localeCompare(b.id),
    );
    const startX = (widest * COL_W - arr.length * COL_W) / 2;
    arr.forEach((n, i) => {
      const x = snap(60 + startX + i * COL_W);
      const y = snap(40 + L * ROW_H);
      placed.set(n.id, x);
      out.set(n.id, { x, y });
    });
  }

  return {
    ...t,
    nodes: nodes.map((n) => {
      const pos = out.get(n.id);
      return pos ? { ...n, x: pos.x, y: pos.y } : n;
    }),
  };
}

/* ---------------------------------------------------------------- builders */

export const DEFAULT_NOMINAL: Nominal = {
  acVoltage: 400,
  dcVoltage: -48,
  frequency: 50,
  pf: 0.95,
};

export function blankTopology(siteId: string | null, siteName: string): SiteTopology {
  return { siteId, siteName, nominal: DEFAULT_NOMINAL, nodes: [], origin: 'blank' };
}

/**
 * Device role -> node kind, with the seed's chain position.
 *
 * A role that is not in this map is not on the power path — a meter, a BMS, a protocol
 * gateway. Seeding those as carriers would put instrumentation in series with the load
 * and make every derived verdict wrong, so they are skipped and reported rather than
 * mapped to something plausible.
 */
export interface RoleCatalogueEntry {
  readonly key: string;
  /** The node kind this role seeds as, or null when the role is not on the power path. */
  readonly nodeKind: string | null;
  readonly chainOrder: number;
  /** Equipment family: power, storage, generation, cooling, load, control, metering, … */
  readonly category: string;
  readonly label?: string | null | undefined;
}

/**
 * The role catalogue this module is currently using.
 *
 * A MUTABLE MODULE-LEVEL OVERRIDE, and that deserves a defence. `device_roles` is the open
 * set — an operator adds a role and it exists — but this file is pure and synchronous, and
 * threading a catalogue argument through `buildTopologyFromDevices`, `roleChainOrder`,
 * `roleIsStorage` and `roleIsLoad` would push it into a dozen call sites that have no
 * business knowing about it.
 *
 * So the app installs the catalogue once, on load, and the hard-coded table below is the
 * fallback for tests and for the moment before the fetch returns. The fallback is NOT the
 * authority: a role seeded in the database and absent here still works, which is the whole
 * point — before this, a new role was reported to the operator as "not on the power path"
 * until somebody edited TypeScript.
 */
let installedRoles: ReadonlyMap<string, RoleCatalogueEntry> | null = null;

export function installRoleCatalogue(roles: readonly RoleCatalogueEntry[]): void {
  installedRoles = new Map(roles.map((r) => [r.key, r]));
}

/** Test seam, so one suite's catalogue cannot leak into the next. */
export function resetRoleCatalogue(): void {
  installedRoles = null;
}

/** The chain placement for a role, from the installed catalogue or the built-in fallback. */
function roleEntry(roleKey: string): { kind: NodeKind; chainOrder: number } | null {
  const live = installedRoles?.get(roleKey);
  if (live !== undefined) {
    /*
     * A role whose `nodeKind` the catalogue does not recognise is treated as OFF the power
     * path rather than mapped to something plausible. A wrong kind is not a cosmetic error:
     * it decides whether the solver treats the device as a source, a carrier or a load.
     */
    const kind = live.nodeKind;
    if (kind === null || !(kind in NODE_KINDS)) return null;
    return { kind: kind as NodeKind, chainOrder: live.chainOrder };
  }
  return ROLE_MAP[roleKey] ?? null;
}

/** The equipment family for a role. Unknown roles are 'other', never guessed. */
export function roleCategory(roleKey: string | null | undefined): string {
  if (!roleKey) return 'other';
  return installedRoles?.get(roleKey)?.category ?? (roleKey in ROLE_MAP ? 'power' : 'other');
}

const ROLE_MAP: Record<string, { kind: NodeKind; chainOrder: number }> = {
  pv_array: { kind: 'solar', chainOrder: 5 },
  combiner: { kind: 'dcbus', chainOrder: 8 },
  utility_feed: { kind: 'utility', chainOrder: 10 },
  inverter: { kind: 'inverter', chainOrder: 12 },
  transformer: { kind: 'transformer', chainOrder: 15 },
  switchgear: { kind: 'switchgear', chainOrder: 20 },
  ats: { kind: 'ats', chainOrder: 25 },
  generator: { kind: 'genset', chainOrder: 30 },
  ups: { kind: 'ups', chainOrder: 40 },
  rectifier: { kind: 'rectifier', chainOrder: 41 },
  battery: { kind: 'battery', chainOrder: 45 },
  static_switch: { kind: 'sts', chainOrder: 47 },
  pdu: { kind: 'pdu', chainOrder: 50 },
  busway: { kind: 'busway', chainOrder: 51 },
  rpp: { kind: 'rpp', chainOrder: 52 },
  dcdu: { kind: 'dcbus', chainOrder: 52 },
  crac: { kind: 'cooling', chainOrder: 60 },
  chiller: { kind: 'cooling', chainOrder: 61 },
  it_load: { kind: 'rack', chainOrder: 70 },
  facility_load: { kind: 'load', chainOrder: 70 },
  bts: { kind: 'tenant', chainOrder: 70 },
  microwave: { kind: 'tenant', chainOrder: 71 },
  charger: { kind: 'evac', chainOrder: 70 },
};

/** Structural subset of the API `Device`, so this file needs no API import. */
export interface SeedDevice {
  readonly id: number;
  readonly deviceName: string;
  readonly label: string | null;
  readonly roleKey: string | null;
  readonly parentDeviceId: number | null;
}

export interface SkippedDevice {
  readonly deviceId: number;
  readonly name: string;
  readonly reason: string;
}

export interface SeedResult {
  readonly topology: SiteTopology;
  readonly skipped: readonly SkippedDevice[];
  /** Things the operator has to decide that the device inventory could not. */
  readonly notes: readonly string[];
}

/**
 * Derives a starting topology from a site's devices: `roleKey` gives the kind,
 * `parentDeviceId` gives the edge.
 *
 * The seed is a starting point, never an answer. It cannot know which utility feed is
 * the A feed, so it PROPOSES A and B for the first two source-role devices and says so.
 * That proposal is safe precisely because the redundancy derivation ignores labels when
 * it tests independence — a wrong guess produces a wrong band colour and an advisory,
 * never a 2N verdict.
 */
/**
 * Where a role sits in the power chain, and whether it can carry through.
 *
 * Exported so a UI that lets someone pick a PARENT can refuse an impossible answer. This is
 * not cosmetic validation: three links written into JAZZMSC5's inventory had two rectifiers
 * fed by BATTERY CABINETS, which inverted the plant. The derivation faithfully concluded the
 * batteries were holding the site up and reported "BATT · On stored energy" with zero
 * effective sources, against a live mains and 236 kW of measured flow. A picker that offers
 * an electrically impossible parent will eventually have it chosen.
 */
export function roleChainOrder(roleKey: string | null | undefined): number | null {
  if (!roleKey) return null;
  return roleEntry(roleKey)?.chainOrder ?? null;
}

/** True when a role is stored energy — a shunt off a bus, never a feed into one. */
export function roleIsStorage(roleKey: string | null | undefined): boolean {
  if (!roleKey) return false;
  const k = roleEntry(roleKey)?.kind;
  return k ? isStorageKind(k) : false;
}

/** True when a role is a load — the end of the chain, so it feeds nothing. */
export function roleIsLoad(roleKey: string | null | undefined): boolean {
  if (!roleKey) return false;
  const k = roleEntry(roleKey)?.kind;
  return k ? isLoadKind(k) : false;
}

export function buildTopologyFromDevices(input: {
  siteId: string | null;
  siteName: string;
  devices: readonly SeedDevice[];
  nominal?: Nominal;
  /**
   * Propose conductors from role order when the device inventory records no parents.
   *
   * WHY THIS IS OPTIONAL AND OFF BY DEFAULT. `parent_device_id` is the only field that says
   * what actually feeds what, and on a real site it is usually empty — nobody has walked the
   * plant yet. Seeding without it produces a scatter of unwired boxes: the verdict reads "no
   * load modelled", no conductor carries anything, and the screen looks broken rather than
   * incomplete.
   *
   * A chain inferred from role order — utility, then conversion, then distribution, then load
   * — is a PROPOSAL, and the caller is required to present it as one. It is right about the
   * shape of a radial plant and wrong about anything with parallel trains, which is most of
   * what this product is for. So it exists to give the operator something to correct rather
   * than something to trust, and `notes` says exactly that.
   */
  inferChain?: boolean;
  /**
   * A TELECOM POWER ROOM'S PLACEMENT, read from its readings by the Power flow's own rule
   * (`modules/overview/dc-chain.ts`), for the inferred chain only; a recorded parent always wins.
   * Role order alone hung JAZZ's -48 V DC systems under its UPS units (ups 40, rectifier 41), which
   * the readings rule out: the DC systems draw about 200 kW, the UPS units carry about 52. So a DC
   * system is placed BESIDE the UPS units, fed as they are, and feeds nothing of the AC chain; a
   * battery hangs on the bus its voltage puts it on (the UPS units' link, or a DC system's -48 V bus).
   */
  dcPlacement?: {
    readonly dcSystems: readonly number[];
    readonly upsBatteries: readonly number[];
    readonly dcBatteries: readonly number[];
    /** The reading that justifies it, for the note ("the DC systems draw 200 kW, the UPS units carry 52"). */
    readonly why?: string | undefined;
  };
}): SeedResult {
  const skipped: SkippedDevice[] = [];
  const notes: string[] = [];

  const kept = input.devices.flatMap((d) => {
    const name = d.label ?? d.deviceName;
    if (!d.roleKey || d.roleKey === 'unassigned') {
      skipped.push({ deviceId: d.id, name, reason: 'No role assigned yet' });
      return [];
    }
    const mapped = roleEntry(d.roleKey);
    if (!mapped) {
      /*
       * Reported with its CATEGORY, because "not on the power path" alone reads as a defect.
       * A smart lock and a protocol gateway are both correctly absent from the chain, and an
       * operator who cannot tell that from a mis-typed role will chase the wrong thing.
       */
      const cat = roleCategory(d.roleKey);
      skipped.push({
        deviceId: d.id,
        name,
        reason:
          cat === 'other'
            ? `Role "${d.roleKey}" is not on the power path`
            : `${cat} equipment — measured, but not a point in the power chain`,
      });
      return [];
    }
    return [{ device: d, name, kind: mapped.kind, chainOrder: mapped.chainOrder }];
  });

  const idFor = new Map<number, string>();
  for (const k of kept) idFor.set(k.device.id, `${NODE_KINDS[k.kind].code}-${k.device.id}`);

  const feeds = new Map<string, string[]>();
  for (const k of kept) feeds.set(idFor.get(k.device.id) ?? '', []);

  for (const k of kept) {
    const parentId = k.device.parentDeviceId;
    if (parentId === null) continue;
    const parentNode = idFor.get(parentId);
    const selfNode = idFor.get(k.device.id);
    if (!selfNode) continue;
    if (!parentNode) {
      notes.push(
        `${k.name} names a parent device that is not on the power path — it is seeded unwired.`,
      );
      continue;
    }
    const list = feeds.get(parentNode);
    if (list) list.push(selfNode);
  }

  /*
   * Chain inference, when asked for and only for nodes nothing already feeds.
   *
   * Devices are grouped by chain order and each group feeds the next: every rectifier feeds
   * every DC bus, every DC bus feeds every load. That is deliberately a fan-out rather than
   * a guess at pairings — proposing "rectifier 1 feeds bus 1" invents a correspondence the
   * data does not contain, while "both rectifiers feed the bus" is the honest reading of a
   * plant where two DC plants share a bus, and is also what makes the redundancy derivation
   * interesting rather than trivially single-path.
   */
  if (input.inferChain) {
    const already = new Set<string>();
    for (const list of feeds.values()) for (const id of list) already.add(id);

    /*
     * STORAGE IS A SHUNT, NOT A LINK IN THE CHAIN.
     *
     * Battery cabinets sort between a rectifier (41) and a PDU (50) by chain order, so a naive
     * tier walk wired rectifier -> battery -> PDU and put the battery IN SERIES with the load.
     * That is electrically wrong and it broke the whole derivation: a battery does not carry
     * through, so everything downstream of it went dead, the battery was judged to be holding
     * the plant up, and JAZZMSC5 reported "BATT · On stored energy" with 0 effective sources
     * while its mains was live and 237 kW was flowing. A battery hangs OFF the bus it backs up.
     *
     * So storage is removed from the series and attached to the nearest upstream tier as a
     * leaf.
     */
    const series = kept.filter((k) => !isStorageKind(k.kind));
    const storage = kept.filter((k) => isStorageKind(k.kind));

    /* A DC system sits in the UPS units' tier, fed as they are, and feeds none of the AC chain below
       (`dcPlacement`): a SIDELINE of the tier, never its carrier. */
    const placed = input.dcPlacement;
    const dcSystems = new Set(placed?.dcSystems ?? []);
    const upsOrder = roleEntry('ups')?.chainOrder ?? 40;
    const sideline = new Set<string>();
    const tiers = new Map<number, string[]>();
    for (const k of series) {
      const id = idFor.get(k.device.id);
      if (!id) continue;
      const order = dcSystems.has(k.device.id) ? upsOrder : k.chainOrder;
      if (dcSystems.has(k.device.id)) sideline.add(id);
      const tier = tiers.get(order) ?? [];
      tier.push(id);
      tiers.set(order, tier);
    }
    const orders = [...tiers.keys()].sort((a, b) => a - b);
    const kindOfId = new Map(kept.map((k) => [idFor.get(k.device.id) ?? '', k.kind]));

    /* Each battery hangs off every node in the closest tier at or above it — the bus it
     * backs up. Attached before the series walk so `already` does not claim them. Where the
     * placement says which bus it is on, it hangs off THAT bus's nodes only: the UPS units, or
     * the DC systems. */
    const upsBatteries = new Set(placed?.upsBatteries ?? []);
    const dcBatteries = new Set(placed?.dcBatteries ?? []);
    for (const b of storage) {
      const id = idFor.get(b.device.id);
      if (!id || already.has(id)) continue;
      const upstreamOrder = [...orders].reverse().find((o) => o <= b.chainOrder);
      if (upstreamOrder === undefined) continue;
      const tier = tiers.get(upstreamOrder) ?? [];
      const onUps = tier.filter((x) => kindOfId.get(x) === 'ups');
      const onDc = tier.filter((x) => sideline.has(x));
      const hosts = upsBatteries.has(b.device.id) && onUps.length > 0 ? onUps : dcBatteries.has(b.device.id) && onDc.length > 0 ? onDc : tier;
      for (const up of hosts) {
        const list = feeds.get(up) ?? [];
        if (!list.includes(id)) list.push(id);
        feeds.set(up, list);
      }
      already.add(id);
    }

    /* Each tier feeds the next from its CARRIERS; a tier of sidelines only passes its own feed on. */
    let wired = 0;
    let feeding: string[] = [];
    for (const order of orders) {
      const tier = tiers.get(order) ?? [];
      const downstream = tier.filter((id) => !already.has(id));
      if (feeding.length > 0 && downstream.length > 0) {
        for (const up of feeding) {
          const list = feeds.get(up) ?? [];
          for (const down of downstream) {
            if (!list.includes(down)) {
              list.push(down);
              wired += 1;
            }
          }
          feeds.set(up, list);
        }
        for (const d of downstream) already.add(d);
      }
      const carriers = tier.filter((id) => !sideline.has(id));
      if (carriers.length > 0) feeding = carriers;
    }
    if (dcSystems.size > 0 && sideline.size > 0) {
      notes.push(
        `PLACED ${sideline.size} -48 V DC system(s) beside the UPS units, fed as they are, not behind them` +
          `${placed?.why ? `: ${placed.why}` : ''}. Their batteries and the UPS units' sit on the bus each one's voltage puts it on.`,
      );
    }

    if (wired > 0) {
      notes.push(
        `PROPOSED ${wired} conductor(s) from role order, because no device records a parent. ` +
          'This is a guess at the shape of a radial plant: correct in sequence, wrong wherever ' +
          'the site has parallel trains. Set the real parents on the devices and re-seed — the ' +
          'redundancy verdict below is only as true as this wiring.',
      );
    }
  }

  /*
   * Only UTILITY FEEDS get an A/B proposal, and only when there are at least two.
   *
   * A generator is not a second path — it backs up the path it sits on — so proposing
   * "utility = A, genset = B" would paint a single-corded site in two colours and invite
   * exactly the false 2N reading this module exists to refuse. Labelling carriers is out
   * for the same reason: their band is reachability, computed in derive.deriveBands.
   */
  const utilityOrder = kept
    .filter((k) => k.kind === 'utility')
    .sort((a, b) => a.chainOrder - b.chainOrder || a.name.localeCompare(b.name));
  const proposal = new Map<number, PathLabel>();
  if (utilityOrder.length >= 2) {
    const a = utilityOrder[0];
    const b = utilityOrder[1];
    if (a) proposal.set(a.device.id, 'A');
    if (b) proposal.set(b.device.id, 'B');
    notes.push(
      `Proposed ${a?.name ?? '?'} as the A feed and ${b?.name ?? '?'} as the B feed. ` +
        'Correct them in the inspector — the derivation tests components, not labels, so a ' +
        'wrong guess changes the band colours and nothing else.',
    );
  } else if (kept.some((k) => isSourceKind(k.kind))) {
    notes.push(
      'Only one utility feed was found, so no A/B claim is proposed. A generator backs up ' +
        'the path it sits on rather than forming a second one.',
    );
  }

  const nodes: TopologyNode[] = kept.map((k) => {
    const spec = NODE_KINDS[k.kind];
    const id = idFor.get(k.device.id) ?? `${spec.code}-${k.device.id}`;
    return {
      id,
      kind: k.kind,
      name: k.name,
      bus: spec.bus,
      path: proposal.get(k.device.id) ?? 'shared',
      x: 0,
      y: 0,
      feeds: feeds.get(id) ?? [],
      ratingKw: spec.defaultKw,
      breaker: { state: 'closed', tripped: false },
      failed: false,
      // Standby. A generator seeded from the device inventory has not been started, and
      // the telemetry that would say otherwise is not wired into the topology model.
      running: false,
      deviceId: k.device.id,
    };
  });

  const orphanLoads = nodes.filter(
    (n) => isLoadKind(n.kind) && !nodes.some((p) => p.feeds.includes(n.id)),
  );
  if (orphanLoads.length > 0) {
    notes.push(
      `${orphanLoads.length} load(s) have no parent device recorded, so they read as unfed. ` +
        'Set the parent on the device, or wire them here.',
    );
  }
  if (nodes.every((n) => n.ratingKw === NODE_KINDS[n.kind].defaultKw)) {
    notes.push(
      'Every rating is a kind default — the device inventory does not carry nameplate kW. ' +
        'Ratings affect the kW shown on a conductor, never the redundancy verdict.',
    );
  }

  const topology: SiteTopology = {
    siteId: input.siteId,
    siteName: input.siteName,
    nominal: input.nominal ?? DEFAULT_NOMINAL,
    nodes,
    origin: 'devices',
  };
  return { topology: layoutTopology(topology), skipped, notes };
}



/*
 * ═══════════════════════════ REMOVED: reference plants and worked examples ═══════════
 *
 * This file used to export five INDUSTRY_PRESETS (data centre, telecom −48V, EV, solar, UPS),
 * their tier variants, `presetTopology()`, and two worked examples (EXAMPLE_2N,
 * EXAMPLE_SHARED_ATS). SLD Studio opened on one of them so the canvas was never empty.
 *
 * They are gone on purpose, and the reason is not tidiness.
 *
 * This module's output is a REDUNDANCY VERDICT, and a verdict is the thing customers act on and
 * auditors ask about. Rendered over fabricated equipment it is indistinguishable on screen from
 * a verdict over the customer's own plant: the same cards, the same conductors, the same
 * "Fault tolerant" chip. An operator who landed on a Tier IV 2N diagram of a plant they do not
 * own — with invented ratings and an invented tier claim — had been misinformed by the tool they
 * bought to prevent exactly that.
 *
 * The empty canvas that motivated the presets is handled honestly instead: `useSiteTopology`
 * returns a NAMED gap ('no-devices', 'no-roles') and the studio renders which fact is missing
 * and what supplies it. That is a worse first impression and a better product.
 *
 * `fromRows` and its `Row` tuple went with them — nothing else called either.
 */

/* ═══════════════════════════════════════ closing the chain from telemetry */

/** What one device's readings say about the chain around it. */
export interface ChainEvidence {
  readonly deviceId: number;
  /** AC input volts, if reported. Non-zero means something upstream is feeding it. */
  readonly acInputV: number | null;
  /** Measured power the device is PASSING ON, in kW, if it reports one. */
  readonly loadKw: number | null;
  /** The metric key behind `loadKw`, so the inferred load can cite it. */
  readonly loadKey: string | null;
}

export interface ChainCompletion {
  readonly topology: SiteTopology;
  readonly notes: readonly string[];
}

/**
 * Add the source and the loads a device inventory does not contain but the telemetry proves.
 *
 * WHY. See the note on `TopologyNode.inferred`. JAZZMSC5 seeds seven carriers and storage
 * units with no utility feed and nothing served, so nothing energises and the diagram reads
 * as a fault when the plant is healthy. This closes the chain using only measurements:
 *
 *   - an incoming MAINS when any node reports AC input voltage. Something is supplying that
 *     voltage; drawing nothing there asserts the opposite.
 *   - one LOAD per carrier that reports the power it is passing on, sized at the measured
 *     figure. "The plant is delivering 111.4 kW" is a measurement; how that splits between
 *     racks and radios is not, so it is drawn as one load and named for what measured it
 *     rather than invented as three.
 *
 * Every added node is `inferred`, and `inferredWhy` names the reading. Nothing is added on
 * a hunch: a site whose telemetry shows neither AC input nor load power gets nothing back,
 * and the honest "no load modelled" verdict stands.
 *
 * Batteries are deliberately NOT given loads. A battery passing current is charging or
 * discharging through the bus it sits on, not serving a load of its own, and inventing one
 * would double-count the plant's demand.
 */
export function completeChainFromTelemetry(
  t: SiteTopology,
  evidence: readonly ChainEvidence[],
  /**
   * The mains as a witness reads it (a transfer switch's source 1), where the site has one: the
   * inferred mains is then live or dead as the witness says, never assumed. Absent: as before.
   */
  mains?: { live: boolean | null; volts: number | null } | null,
): ChainCompletion {
  const byDevice = new Map(evidence.map((e) => [e.deviceId, e]));
  const notes: string[] = [];

  /* Who feeds each node, so "is there a grid supply anywhere above it" can be asked. */
  const parents = new Map<string, string[]>();
  for (const n of t.nodes) for (const f of n.feeds) parents.set(f, [...(parents.get(f) ?? []), n.id]);
  const kindOf = new Map(t.nodes.map((n) => [n.id, n.kind]));
  const above = (id: string, hit: (p: string) => boolean): boolean => {
    const seen = new Set<string>();
    const walk = (x: string): boolean =>
      (parents.get(x) ?? []).some((p) => {
        if (seen.has(p)) return false;
        seen.add(p);
        return hit(p) || walk(p);
      });
    return walk(id);
  };

  const hasAc = (n: TopologyNode): boolean => {
    const e = n.deviceId === null ? undefined : byDevice.get(n.deviceId);
    return e?.acInputV !== null && e?.acInputV !== undefined && Math.abs(e.acInputV) > 5;
  };

  /*
   * Which nodes have evidence of a feed no drawn source explains. Only those get a mains: a node
   * with no AC input reading might genuinely be unwired, and connecting it would manufacture the
   * energisation this function is supposed to justify.
   *
   * NOT ONLY ORPHANS (2026-10-20). A node nothing feeds needs a mains, as before; so does one with
   * no GRID supply anywhere above it, whose only sources are standby generators. JAZZ MSC5's two
   * gensets became roles, so the inferred chain hung its UPS and DC plants under them and nothing
   * was an orphan: no mains was added, both sets were OFF, and the diagram read "FAULT, load lost
   * 238 kW" over a room drawing 259 kW on the grid. A stopped set does not explain a live AC input.
   * The mains is given to the TOPMOST such nodes only, so it hangs where the sets do.
   */
  const needs = t.nodes.filter(
    (n) => !isSourceKind(n.kind) && !isStorageKind(n.kind) && hasAc(n) && !above(n.id, (p) => kindOf.get(p) === 'utility'),
  );
  const needIds = new Set(needs.map((n) => n.id));
  const withAcInput = needs.filter((n) => !above(n.id, (p) => needIds.has(p)));

  const added: TopologyNode[] = [];
  const feedsPatch = new Map<string, string[]>();

  if (withAcInput.length > 0) {
    const volts = withAcInput
      .map((n) => (n.deviceId === null ? null : byDevice.get(n.deviceId)?.acInputV ?? null))
      .filter((v): v is number => v !== null);
    const nominal = volts.length > 0 ? Math.round(Math.max(...volts)) : t.nominal.acVoltage;

    added.push({
      id: 'INF-MAINS',
      kind: 'utility',
      name: 'Mains (inferred)',
      bus: 'ac',
      path: 'shared',
      x: 0,
      y: 0,
      feeds: withAcInput.map((n) => n.id),
      /*
       * Sized at the total the plant is measured to be passing on, not at a guessed
       * capacity. An inferred node must not carry an invented rating: this one's rating is
       * the sum of what its children measure, so the flow it shows is arithmetic on
       * measurements rather than a nameplate nobody entered.
       */
      ratingKw: Math.max(
        1,
        Math.round(
          evidence.reduce((sum, e) => sum + (e.loadKw !== null ? Math.abs(e.loadKw) : 0), 0),
        ),
      ),
      breaker: { state: 'closed', tripped: false },
      /* Dead only when a witness of the mains says so; a feed nobody witnesses keeps the old reading. */
      failed: mains?.live === false,
      running: false,
      deviceId: null,
      inferred: true,
      inferredWhy:
        `Not a device. ${withAcInput.length} unit(s) report ~${nominal} V of AC input, which requires a feed.` +
        (mains?.volts != null ? ` The ATS (automatic transfer switch) reads the mains at ~${Math.round(mains.volts)} V${mains.live === false ? ', which is a loss' : ''}.` : ''),
    });
    notes.push(
      `Added an INFERRED mains feed: ${withAcInput
        .map((n) => n.name)
        .join(', ')} report AC input voltage, so something upstream is supplying them. No device ` +
        'row exists for it — onboard the incoming feed to replace this with the real thing.',
    );
  }

  /* One inferred load per carrier that measures the power it passes on. */
  let loadCount = 0;
  let loadTotal = 0;
  for (const n of t.nodes) {
    if (n.deviceId === null) continue;
    if (isStorageKind(n.kind) || isSourceKind(n.kind) || isLoadKind(n.kind)) continue;
    const e = byDevice.get(n.deviceId);
    if (!e || e.loadKw === null || Math.abs(e.loadKw) < 0.5) continue;
    /*
     * Skip only when this node already feeds a LOAD — that load IS its measured demand, and
     * adding a second would double-count.
     *
     * Testing `feeds.length > 0` was too strict: the role-order chain inference gives a
     * rectifier children (a battery, a downstream board), so both DC plants were skipped and
     * their measured 111.4 kW and 88.1 kW of DC load never appeared. Feeding a battery is not
     * serving a load.
     */
    if (n.feeds.some((f) => { const c = t.nodes.find((x) => x.id === f); return c ? isLoadKind(c.kind) : false; })) {
      continue;
    }

    const kw = Math.abs(e.loadKw);
    const id = `INF-LOAD-${n.deviceId}`;
    added.push({
      id,
      kind: n.bus === 'dc' ? 'tenant' : 'rack',
      name: `Load on ${n.name}`,
      bus: n.bus,
      path: 'shared',
      x: 0,
      y: 0,
      feeds: [],
      ratingKw: Math.round(kw * 10) / 10,
      breaker: { state: 'closed', tripped: false },
      failed: false,
      running: false,
      deviceId: null,
      inferred: true,
      inferredWhy: `Not a device. ${n.name} measures ${kw.toFixed(1)} kW leaving it${
        e.loadKey ? ` (${e.loadKey})` : ''
      }, so something is drawing it.`,
    });
    feedsPatch.set(n.id, [...n.feeds, id]);
    loadCount += 1;
    loadTotal += kw;
  }

  if (loadCount > 0) {
    notes.push(
      `Added ${loadCount} INFERRED load(s) totalling ${loadTotal.toFixed(1)} kW, sized at what the ` +
        'plant measures itself delivering. The split between individual loads is not measured, so ' +
        'each is drawn as one block named for the unit that metered it.',
    );
  }

  if (added.length === 0) {
    return { topology: t, notes: [] };
  }

  const nodes = t.nodes.map((n) => {
    const patch = feedsPatch.get(n.id);
    return patch ? { ...n, feeds: patch } : n;
  });

  return { topology: { ...t, nodes: [...nodes, ...added] }, notes };
}
